import SkillsForm from '@/components/dashboard/members/profile/skills/SkillsForm'
import {
  ProfessionalSkills as ProfessionalSkillsT,
  RemoveProfessionalSkillDocument,
} from '@/types/graphql/types'
import { getErrorMessage } from '@/util/getErrorMessage'
import { useMutation } from '@apollo/client'
import { Text, useMantineTheme } from '@mantine/core'
import { useModals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { parseInt } from 'lodash'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'

const useSkillsItem = ({
  ps,
  handleUpdatePs,
}: {
  ps: ProfessionalSkillsT
  handleUpdatePs: ({ newPS }: { newPS: ProfessionalSkillsT[] }) => void
}) => {
  const { colorScheme } = useMantineTheme()
  const isDark = colorScheme === 'dark'
  const { t, lang } = useTranslation('common')
  const isArabic = lang === 'ar'
  const modals = useModals()
  const { query } = useRouter()

  //open skills modal
  const openPSForm = (p: ProfessionalSkillsT) =>
    modals.openModal({
      title: t('scientificResearches'),
      children: <SkillsForm ps={p} handleUpdatePs={handleUpdatePs} />,
    })
  //remove skill mutation
  const [removePS, { loading }] = useMutation(RemoveProfessionalSkillDocument, {
    onCompleted(data) {
      handleUpdatePs({
        newPS: data.removeProfessionalSkill as ProfessionalSkillsT[],
      })
      modals.closeAll()
      showNotification({
        title: t('removed'),
        message: t('removedSuccessfully'),
      })
    },
    onError(error) {
      showNotification({
        title: t('error'),
        message: getErrorMessage(error),
        color: 'red',
        autoClose: false,
      })
    },
  })
  //confirm remove skill
  const openDeleteMessage = (id: number) =>
    modals.openConfirmModal({
      title: t('education'),
      children: <Text size="sm">{t('deletePSMessage')}</Text>,
      labels: { confirm: t('confirm'), cancel: t('cancel') },
      async onConfirm() {
        await removePS({
          variables: {
            data: {
              userId: parseInt(query.id as string),
              ProfessionalSkillId: id,
            },
          },
        })
      },
    })

  const academicRank = isArabic
    ? ps.academicRank?.titleAr
    : ps.academicRank?.titleEn

  return {
    openDeleteMessage,
    academicRank,
    openPSForm,
    loading,
  }
}

export default useSkillsItem
